import { Params } from '@angular/router';
import { ISerializer, isDefined, isNonEmptyString } from '@fmnts/core';
import { Codec, ParseResult } from '@fmnts/io';
import * as DateTime from 'effect/DateTime';
import * as Either from 'effect/Either';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import moment from 'moment';
import { QueryParamsSerializer } from './query-param.adapter';

const MomentFromStringCodec = (opts: {
  format: string;
}): Codec.Codec<moment.Moment, string> => ({
  decode: (input) => {
    const parsed = moment(input, opts.format, true);
    return parsed.isValid()
      ? Either.right(parsed)
      : Either.left(
          new ParseResult.Transformation(
            input,
            `Expected: Date in format ${opts.format}`,
          ),
        );
  },
  encode: (value: moment.Moment) => {
    if (moment.isMoment(value)) {
      return Either.right(value.format(opts.format));
    } else if (typeof value === 'string') {
      const parsed = moment(value);
      return parsed.isValid()
        ? Either.right(parsed.format(opts.format))
        : Either.left(new ParseResult.Transformation(value, 'Expected: Date'));
    }
    return Either.left(new ParseResult.Transformation(value, 'Expected: Date'));
  },
});
const PlainDateFromStringCodec: Codec.Codec<Date, string> = {
  decode: (input) =>
    Fn.pipe(
      DateTime.make(input),
      Either.fromOption(
        () =>
          new ParseResult.Transformation(input, `Expected: Date in iso format`),
      ),
      Either.map(DateTime.toDate),
    ),
  encode: (input) =>
    Fn.pipe(
      DateTime.make(input),
      Either.fromOption(
        () => new ParseResult.Transformation(input, `Expected: Date`),
      ),
      Either.map(DateTime.formatIsoDate),
    ),
};

const momentOptionCodec = Codec.OptionFromNullishOr(
  MomentFromStringCodec({
    format: 'YYYY-MM-DD',
  }),
  undefined,
);
export const PlainDateOptionFromStringCodec = Codec.OptionFromNullishOr(
  PlainDateFromStringCodec,
  undefined,
);

class PlainDateQueryParamSerializer<A>
  implements ISerializer<A | undefined, string | undefined>
{
  constructor(
    private readonly codec: Codec.OptionFromNullishOr<Codec.Codec<A, string>>,
  ) {}

  serialize(data: A | undefined): string | undefined {
    return Fn.pipe(
      O.fromNullable(data),
      Codec.encodeEither(this.codec),
      Either.getOrElse(() => undefined),
      O.fromNullable,
      O.getOrUndefined,
    );
  }

  deserialize(s: unknown): A | undefined {
    return Fn.pipe(
      s,
      O.liftPredicate(isNonEmptyString),
      O.getOrNull,
      Codec.decodeEither(this.codec),
      Either.getOrElse(() => O.none()),
      O.getOrUndefined,
    );
  }
}

export const plainDateQueryParamSerializer = new PlainDateQueryParamSerializer(
  PlainDateOptionFromStringCodec,
);
/**
 * @deprecated prefer {@link plainDateQueryParamSerializer}
 */
export const dateQueryParamSerializer = new PlainDateQueryParamSerializer(
  momentOptionCodec,
);

export class PlainDateRangeQueryParamsSerializer<A>
  implements QueryParamsSerializer<readonly [A, A] | undefined>
{
  constructor(
    private readonly startKey: string,
    private readonly endKey: string,
    private readonly dateSerializer: PlainDateQueryParamSerializer<A>,
  ) {}

  serialize(data: readonly [A, A] | undefined): Params {
    const [start, end] = data ?? [];
    return {
      [this.startKey]: this.dateSerializer.serialize(start),
      [this.endKey]: this.dateSerializer.serialize(end),
    };
  }

  deserialize(params: Params): readonly [A, A] | undefined {
    const startValue: unknown = params[this.startKey];
    const endValue: unknown = params[this.endKey];
    const start = this.dateSerializer.deserialize(startValue);
    const end = this.dateSerializer.deserialize(endValue);
    return isDefined(start) && isDefined(end) ? [start, end] : undefined;
  }
}

/**
 * @deprecated prefer {@link PlainDateRangeQueryParamsSerializer}
 */
export class DateRangeQueryParamsSerializer extends PlainDateRangeQueryParamsSerializer<moment.Moment> {
  constructor(startKey: string, endKey: string) {
    super(startKey, endKey, dateQueryParamSerializer);
  }
}
